import { createSelector } from "reselect";
import { observeState } from "@mapsight/lib-redux/observe-state";
import { mergeAll } from "@mapsight/core/lib/base/actions";
import { createFilteredFeatureSourceSelector } from "@mapsight/core/lib/feature-sources/selectors";
import { activateInteraction, deactivateInteraction, } from "@mapsight/core/lib/map/actions";
import { DEFAULT_DISPLAY_STYLE, DEFAULT_PROJECTION, } from "@mapsight/core/mixins/EditorMixin";
import { di } from "@mapsight/core/ol-proxy";
import DrawInteraction from "@mapsight/core/ol-proxy/definitions/interaction/DrawInteraction";
import { FEATURE_SELECTIONS, FEATURE_SOURCES, MAP, } from "../../config/constants/controllers";
import { getDictionary } from "../../helpers/i18n";
export const createActivateAction = (mapController, name) => activateInteraction(mapController, `${name}_drawInteraction`);
export const createDeactivateAction = (mapController, name) => deactivateInteraction(mapController, `${name}_drawInteraction`);
/**
 * @param {object} options options
 * @param {import('redux').Store} options.store store
 * @param {string} options.name name name of the editor (must be unique per mapsight instance)
 * @param {string} options.mapControllerName map controller name
 * @param {string} options.featureSourcesControllerName feature sources controller name
 * @param {string} options.featureSelectionsControllerName feature selections controller name
 * @param {string} options.displayStyle style for finished draw
 * @param {string} options.drawStyle style while drawing
 */
function setupDrawInteraction({ store, name, featureSourcesControllerName, featureSelectionsControllerName, mapControllerName, drawStyle, displayStyle, }) {
    const fSId = `${name}_featureSource`;
    const drawLayerId = `${name}_drawLayer`;
    const interactionId = `${name}_drawInteraction`;
    const vFS = {
        type: "VectorFeatureSource",
        options: {
            projection: DEFAULT_PROJECTION,
            featureSourceId: fSId,
            featureSourcesControllerName: featureSourcesControllerName,
            featureSelectionsControllerName: featureSelectionsControllerName,
            canAnimate: false,
            canCluster: false,
            featureSelections: [],
        },
    };
    store.dispatch(mergeAll({
        [mapControllerName]: {
            interactions: {
                [interactionId]: {
                    type: "DrawInteraction",
                    options: {
                        active: false,
                        type: "LineString",
                        style: drawStyle,
                        replacePrevious: true,
                        clearOnStart: true,
                        source: vFS,
                        stopClick: true,
                        measure: {
                            active: true,
                            keepLabel: true,
                        },
                    },
                },
            },
            layers: {
                [drawLayerId]: {
                    type: "VectorLayer",
                    options: {
                        visible: true,
                        style: displayStyle,
                        renderBuffer: 200,
                        selections: [],
                        source: vFS,
                    },
                },
            },
        },
        [featureSourcesControllerName]: {
            [fSId]: {
                enableHistory: false,
                data: {},
                isLoading: true,
            },
        },
    }));
    const featuresSelector = createSelector(createFilteredFeatureSourceSelector(featureSourcesControllerName, fSId, mapControllerName), (featureSourceState) => { var _a; return (_a = featureSourceState === null || featureSourceState === void 0 ? void 0 : featureSourceState.data) === null || _a === void 0 ? void 0 : _a.features; });
    const deactivate = createDeactivateAction(mapControllerName, name);
    observeState(store, featuresSelector, (createdFeatures) => {
        var _a;
        if (createdFeatures === null || createdFeatures === void 0 ? void 0 : createdFeatures.length) {
            const feature = createdFeatures[0];
            if (((_a = feature.geometry) === null || _a === void 0 ? void 0 : _a.type) === "Point") {
                store.dispatch(deactivate);
            }
        }
    });
}
/**
 * This plugin will allow drawing a link maker (to be used with the appropriate component)
 * and/or display such a measurement.
 *
 * @param {object} [options] options
 * @param {string} [options.name="measureDistance"] name name of the editor (must be unique per mapsight instance)
 * @param {string} [options.mapControllerName="map"] map controller name
 * @param {string} [options.featureSourcesControllerName="featureSources"] feature sources controller name
 * @param {string} [options.featureSelectionsControllerName="featureSelections"] feature selections controller name
 * @param {boolean} [options.enableDrawing=true] enable drawing
 * @param {string} [options.displayStyle="features"] style for finished draw
 * @param {string} [options.drawStyle="drawMeasure"] style while drawing
 * @param {import('ol/interaction/draw').Draw | null} [options.drawInteraction] draw interaction
 * @returns {import('../../types').PluginInstance} plugin
 */
export default function createMeasureDistancePlugin(options = {}) {
    const { name = "measureDistance", enableDrawing = true, mapControllerName = MAP, featureSourcesControllerName = FEATURE_SOURCES, featureSelectionsControllerName = FEATURE_SELECTIONS, displayStyle = DEFAULT_DISPLAY_STYLE, drawStyle = "drawMeasure", drawInteraction = DrawInteraction, } = options;
    const dict = getDictionary();
    Object.assign(dict.de, {
        "ui.measure-distance.title": "Strecke messen",
        "ui.measure-distance.instructions": "Klicken Sie auf die Karte um Punkte hinzuzufügen. Ein Doppelklick schließt die Strecke ab.",
        "ui.measure-distance.instructions-done": "Klicken Sie auf die Karte um eine neue Strecke zu messen.",
        "ui.measure-distance.measurement": "Streckenlänge",
    });
    Object.assign(dict.en, {
        "ui.measure-distance.title": "Measure distance",
        "ui.measure-distance.instructions": "Click on the map to add points. Double click to end the line.",
        "ui.measure-distance.instructions-done": "Click on the map to start measuring a new line.",
        "ui.measure-distance.measurement": "Distance",
    });
    return {
        afterCreate: function measureDistancePlugin({ store }) {
            if (enableDrawing) {
                if (drawInteraction) {
                    di.injectDefinitions([drawInteraction]);
                }
                setupDrawInteraction({
                    name: name,
                    store: store,
                    mapControllerName: mapControllerName,
                    featureSourcesControllerName: featureSourcesControllerName,
                    featureSelectionsControllerName: featureSelectionsControllerName,
                    drawStyle: drawStyle,
                    displayStyle: displayStyle,
                });
            }
        },
    };
}
